import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'
import { MONO, TEXT_CAPTION } from '../recipes'
import { VoiceGlyph } from './VoiceGlyph'
import { originText } from './voiceprint-format'
import type { VoiceprintTagData } from './voiceprint-types'

export interface VoiceprintTagProps {
  voiceprint: VoiceprintTagData
  /** 20 px in dense rows, 28 px elsewhere: the mark is never set larger beside another one. */
  size?: 20 | 28
  /**
   * Draw the mark's rings in, once, when the voiceprint turns kept: for a tag that stays on the
   * page while its voiceprint is being computed (a voice just assigned).
   */
  drawIn?: boolean
  className?: string
}

/**
 * One voiceprint named in a line: its mark, its short id in mono and where it came from —
 * "◎ 7f3a · from a questioning · Voice 2". This is how a voiceprint is shown wherever two are
 * compared (a lead's row, the comparison's columns, a proposal): small, as a tag beside the
 * words, so the two marks are never read against each other as pictures. A voiceprint
 * comparisons do not use gets the muted mark and says why.
 */
export function VoiceprintTag({
  voiceprint,
  size = 28,
  drawIn = false,
  className,
}: VoiceprintTagProps) {
  const { t } = useTranslation()
  const { id, shortId, origin, state, voice = null, used = true } = voiceprint
  const inUse = state === 'ready' && used
  const reason =
    state === 'ready' ? 'humint.voice.mark.use.unused' : `humint.voice.mark.reason.${state}`
  return (
    <span
      data-voiceprint={shortId}
      className={cn('inline-flex min-w-0 items-center gap-2', className)}
    >
      <VoiceGlyph id={id} size={size} form={inUse ? 'used' : 'unused'} drawIn={drawIn} />
      <span className={cn(TEXT_CAPTION, 'flex min-w-0 flex-wrap items-baseline gap-x-1.5')}>
        <span className="sr-only">{t('humint.voice.mark.label', { id: shortId })}</span>
        <span aria-hidden className={cn(MONO, 'font-semibold text-foreground')}>
          {shortId}
        </span>
        <span className="text-muted-foreground">
          {originText(origin, voice, t)}
          {!inUse && ` · ${t(reason)}`}
        </span>
      </span>
    </span>
  )
}
